import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { useRecoilState } from 'recoil';
import {
  contractAddressErrorState,
  contractAddressesState,
  litTokenContractAddressState,
} from '../../context/appContext';
import getLitTokenContractAddress, {
  networkNames,
  networks,
  chainNames,
} from './utils';
import {
  switchToYellowstoneNetwork,
  switchToLitChainNetwork,
} from '../../lib/web3';
import { getSigner } from '../../lib/web3';
import { getNodeInfo, getSelfStake } from '../../services/staking';
import { ethers } from 'ethers';

type NodeState = 'onboarding' | 'active' | 'offboarding' | 'inactive';

function SetContractAddresses() {
  const [stakingContractAddresses, setStakingContractAddresses] =
    useRecoilState(contractAddressesState);
  const [litTokenContractAddress, setLitTokenContractAddress] = useRecoilState(
    litTokenContractAddressState
  );
  const [contractAddressError] = useRecoilState(contractAddressErrorState);

  // Validator state
  const [stakeAddress, setStakeAddress] = useState<string>('');
  const [validatorIp, setValidatorIp] = useState<string | null>(null);
  const [nodeState, setNodeState] = useState<NodeState | null>(null);
  const [selfStakeAmount, setSelfStakeAmount] = useState<ethers.BigNumber | null>(null);
  const [isLoadingValidatorState, setIsLoadingValidatorState] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // we now set the default in appContext.ts instead of here.
    if (stakingContractAddresses.chain.name === chainNames.YELLOWSTONE) {
      switchToYellowstoneNetwork();
    } else if (stakingContractAddresses.chain.name === chainNames.LIT_CHAIN) {
      switchToLitChainNetwork();
    }
  }, [stakingContractAddresses]);

  useEffect(() => {
    if (stakingContractAddresses.stakingAddress !== '') {
      // not Custom network
      (async () => {
        setLitTokenContractAddress(
          await getLitTokenContractAddress(stakingContractAddresses)
        );
      })();
    } else {
      setLitTokenContractAddress('');
    }
  }, [stakingContractAddresses, setLitTokenContractAddress]);

  function updateStakingContractAddresses(event: SelectChangeEvent) {
    setStakingContractAddresses(
      networks[event.target.value as keyof typeof networks]
    ); // Only used in the below Select component
  }

  function handleStakingAddressChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    setStakingContractAddresses({
      ...stakingContractAddresses,
      stakingAddress: event.target.value,
    });
  }

  const getNodeState = (
    inCurrentEpoch: boolean,
    inNextEpoch: boolean
  ): NodeState => {
    if (inCurrentEpoch && inNextEpoch) {
      return 'active';
    } else if (!inCurrentEpoch && inNextEpoch) {
      return 'onboarding';
    } else if (inCurrentEpoch && !inNextEpoch) {
      return 'offboarding';
    } else {
      return 'inactive';
    }
  };

  const updateValidatorState = useCallback(async () => {
    if (!stakingContractAddresses.stakingAddress) {
      setStakeAddress('');
      setValidatorIp(null);
      setNodeState(null);
      setSelfStakeAmount(null);
      return;
    }

    try {
      setIsLoadingValidatorState(true);
      const signer = await getSigner();
      const addr = await signer.getAddress();
      setStakeAddress(addr);

      // Get validator info
      const nodeInfo = await getNodeInfo(
        stakingContractAddresses.stakingAddress,
        addr
      );

      if (nodeInfo) {
        // Set IP (or "no validator info" if IP is 0.0.0.0 or empty)
        const ip = nodeInfo.nodeInfo.ip;
        if (ip && ip !== '0.0.0.0' && ip !== '') {
          setValidatorIp(ip);
        } else {
          setValidatorIp(null);
        }

        // Determine node state
        const state = getNodeState(
          nodeInfo.inCurrentEpoch,
          nodeInfo.inNextEpoch
        );
        setNodeState(state);
      } else {
        // No validator info found
        setValidatorIp(null);
        setNodeState(null);
      }

      // Get self-stake amount
      try {
        const selfStake = await getSelfStake(
          stakingContractAddresses.stakingAddress,
          addr
        );
        setSelfStakeAmount(selfStake);
      } catch (e: any) {
        console.error('Error getting self-stake:', e);
        setSelfStakeAmount(ethers.BigNumber.from(0));
      }
    } catch (e: any) {
      console.error('Error updating validator state:', e);
      // If wallet not connected or other error, clear state
      setStakeAddress('');
      setValidatorIp(null);
      setNodeState(null);
      setSelfStakeAmount(null);
    } finally {
      setIsLoadingValidatorState(false);
    }
  }, [stakingContractAddresses.stakingAddress]);

  useEffect(() => {
    // Initial update
    updateValidatorState();

    // Set up interval to update every 10 seconds
    intervalRef.current = setInterval(() => {
      updateValidatorState();
    }, 10000);

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [updateValidatorState]);

  return (
    <Card>
      <CardContent>
        <Stack spacing={2} sx={{ m: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant={'h5'}>Choose Network</Typography>
            <Select
              value={stakingContractAddresses.network}
              onChange={updateStakingContractAddresses}
              displayEmpty
              sx={{ width: '8em' }}
            >
              {Object.values(networkNames).map((networkName) => (
                <MenuItem value={networkName}>{networkName}</MenuItem>
              ))}
            </Select>
          </div>
          {stakingContractAddresses.network === networkNames.CUSTOM && (
            <Stack spacing={2} direction={'column'} sx={{ m: 2 }}>
              <TextField
                sx={{ flexGrow: '1', flexShrink: '1' }}
                size={'small'}
                value={stakingContractAddresses.stakingAddress}
                onChange={handleStakingAddressChange}
                label={'Staking Contract Address'}
                placeholder={'eg. 0xf899FcCE61F48211aa9b36Dc39093951E6028698'}
              />
              <TextField
                sx={{ flexGrow: '1', flexShrink: '1' }}
                size={'small'}
                value={litTokenContractAddress}
                onChange={(e) => setLitTokenContractAddress(e.target.value)}
                label={'Lit Token Contract Address'}
                placeholder={'eg. 0x74eD0E2951Cc0d56C9595A19b26AC4CCC376E2F6'}
              />
              {/*<Button*/}
              {/*  variant={'contained'}*/}
              {/*  disabled={_litTokenContractAddress === litTokenContractAddress && _stakingContractAddress === stakingContractAddress}*/}
              {/*  onClick={handleUpdateContractAddresses}>Save</Button>*/}
            </Stack>
          )}
          <Typography color={'error'}>{contractAddressError}</Typography>

          {/* Validator State Section */}
          {stakingContractAddresses.stakingAddress && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
              }}
            >
              <Typography variant={'h6'} sx={{ mb: 1 }}>
                Current Validator State
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant={'body2'} color={'text.secondary'}>
                    Stake Address (Change via wallet connection):
                  </Typography>
                  <Typography variant={'body1'}>
                    {isLoadingValidatorState
                      ? 'Loading...'
                      : stakeAddress || 'Not connected'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant={'body2'} color={'text.secondary'}>
                    Self-Staked WLIT:
                  </Typography>
                  <Typography variant={'body1'}>
                    {isLoadingValidatorState
                      ? 'Loading...'
                      : selfStakeAmount !== null
                        ? ethers.utils.formatUnits(selfStakeAmount, 18)
                        : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant={'body2'} color={'text.secondary'}>
                    IP:
                  </Typography>
                  <Typography variant={'body1'}>
                    {isLoadingValidatorState
                      ? 'Loading...'
                      : validatorIp || 'no validator info'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant={'body2'} color={'text.secondary'}>
                    Node State:
                  </Typography>
                  {isLoadingValidatorState ? (
                    <Typography variant={'body1'}>Loading...</Typography>
                  ) : nodeState ? (
                    <Chip
                      label={nodeState}
                      color={
                        nodeState === 'active'
                          ? 'success'
                          : nodeState === 'onboarding'
                            ? 'info'
                            : nodeState === 'offboarding'
                              ? 'warning'
                              : 'default'
                      }
                      size="small"
                    />
                  ) : (
                    <Typography variant={'body1'}>-</Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default SetContractAddresses;
